// src/components/CartDropdown.js
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VerifiedIcon from '@mui/icons-material/Verified';
import { ShoppingBasket } from 'lucide-react';
import { getStorefrontProducts } from '../db';
import { ProductThumb } from './ProductPhoto';
import { cartOrder, readStoredCart, reconcileCart, writeStoredCart } from '../products';

const rand = (value) => `R${Number(value || 0).toFixed(2)}`;

const CartDropdown = ({ onClose }) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const dropdownRef = useRef(null);

  // Seeded from storage so the first paint already shows what is in the cart,
  // named and priced as it was last shown; the live figures arrive with the
  // fetch below.
  const [cart, setCart] = useState(() => readStoredCart());
  const [products, setProducts] = useState([]);
  // Empty, which reads as zero: until the live figure lands the + button must
  // stay disabled rather than let someone order past the stock on hand.
  const [stock, setStock] = useState({});

  /**
   * Live prices and stock, with each quantity trimmed to what can be sold —
   * anything off sale or out of stock drops out of the cart.
   *
   * This runs once, on mount. It reads the quantities out of localStorage
   * rather than out of state on purpose: they are the same values at mount —
   * state is seeded from there — but taking them from state would make the
   * quantities dependencies of the fetch, so every plus and minus click would
   * refetch, and the write at the end would retrigger the effect that caused
   * it. Reading the stored cart keeps the effect honestly mount-only.
   */
  useEffect(() => {
    let active = true;
    async function fetchLiveData() {
      const stored = readStoredCart();
      let next = stored;

      try {
        const data = await getStorefrontProducts();
        if (!active) return;
        const live = reconcileCart(stored, data);
        next = { items: live.items, prices: live.prices, names: live.names };
        setProducts(data);
        setStock(live.stock);
      } catch (err) {
        console.error('Error fetching live data:', err.message);
        if (!active) return;
        // Nothing is known to be off sale, so the stored quantities stand, at
        // the prices they were last shown at.
      }

      setCart(next);
      // Stored after the fetch, so the cart carries the prices it was
      // actually shown at.
      writeStoredCart(next.items, next.prices, next.names);
    }
    fetchLiveData();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const setQuantity = (product, value) => {
    const items = { ...cart.items, [product.id]: Math.max(0, value) };
    const next = { ...cart, items };
    setCart(next);
    writeStoredCart(items, cart.prices, cart.names);
  };

  const lines = cartOrder(cart.items, products)
    .map((id) => {
      const quantity = cart.items[id] || 0;
      const price = cart.prices[id] || 0;
      const product = { id, name: cart.names[id] || '' };
      return { product, quantity, price, subtotal: quantity * price };
    })
    .filter((line) => line.quantity > 0);

  const totalItems = lines.reduce((sum, line) => sum + line.quantity, 0);
  const grandTotal = lines.reduce((sum, line) => sum + line.subtotal, 0);

  return (
    <Paper
      ref={dropdownRef}
      elevation={0}
      sx={{
        position: 'absolute',
        top: 'calc(100% + 10px)',
        right: 0,
        width: { xs: 320, sm: 380 },
        maxHeight: '78vh',
        overflowY: 'auto',
        p: 2.5,
        zIndex: 1000,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: dark
          ? '0 18px 44px rgba(0,0,0,0.55)'
          : '0 18px 44px rgba(60,54,40,0.16)',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Your cart</Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'success.main' }}>
          <VerifiedIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em' }}>
            Field Fresh
          </Typography>
        </Stack>
      </Stack>

      {totalItems > 0 ? (
        <>
          <Stack spacing={1.75}>
            {lines.map(({ product, quantity, price, subtotal }) => {
              const limit = stock[product.id] || 0;
              const atLimit = quantity >= limit;
              return (
                <Box
                  key={product.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '52px 1fr',
                    gap: 1.5,
                    alignItems: 'center',
                  }}
                >
                  <ProductThumb
                    productId={product.id}
                    alt={product.name}
                    size={52}
                    sx={{ width: 52, height: 52, borderRadius: 2 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.3 }}>
                          {product.name}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                          {rand(price)} each
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        aria-label={`Remove ${product.name} from cart`}
                        onClick={() => setQuantity(product, 0)}
                        sx={{ mt: -0.5, color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Stack>

                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mt: 0.75 }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                          borderRadius: 999,
                          border: '1px solid',
                          borderColor: 'divider',
                          px: 0.25,
                        }}
                      >
                        <IconButton
                          size="small"
                          aria-label={`Remove one ${product.name}`}
                          onClick={() => setQuantity(product, quantity - 1)}
                        >
                          <RemoveIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                        <Typography sx={{ minWidth: 20, textAlign: 'center', fontWeight: 700, fontSize: '0.85rem' }}>
                          {quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={`Add one ${product.name}`}
                          disabled={atLimit}
                          onClick={() => setQuantity(product, quantity + 1)}
                        >
                          <AddIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Stack>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
                        {rand(subtotal)}
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
              Items in cart
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{totalItems}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography sx={{ fontWeight: 700 }}>Total</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.3rem' }}>{rand(grandTotal)}</Typography>
          </Stack>

          <Button
            component={Link}
            to="/checkout"
            variant="contained"
            fullWidth
            disableElevation
            endIcon={<ArrowForwardIcon />}
            onClick={onClose}
            sx={{ mt: 2, py: 1.3, borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
          >
            Proceed to checkout
          </Button>
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: 'auto',
              mb: 2,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '50%',
              color: 'primary.main',
              backgroundColor: dark ? 'rgba(179,199,127,0.12)' : 'rgba(85,107,47,0.09)',
            }}
          >
            <ShoppingBasket size={26} strokeWidth={1.6} />
          </Box>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>Your cart feels a bit light today</Typography>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            Browse hay bales
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default CartDropdown;
